import React, {useEffect} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Flex,
} from '@chakra-ui/react';
import {useAgentBuilderStore} from './AgentBuilder.state';
import {AgentBuilderSetNameStep} from './AgentBuilderSetNameStep';
import {ObjectId} from 'bson';
import {useAgent} from '../../../hooks/api/metadata';
import {AgentBuilderSelectAccountsStep} from './AgentBuilderSelectAccountsStep';
import {
  AgentBuilderStep,
  useAgentBuilderNavStore,
} from './AgentBuilderNav.state';
import {AgentBuilderEditLegacyResearchFieldStep} from './AgentBuilderEditLegacyResearchFieldStep';
import {AgentBuilderEditAnalystFieldStep} from './AgentBuilderEditAnalystFieldStep';
import {AgentBuilderAnalyzeListsStep} from './AgentBuilderAnalyzeListsStep';
import {AgentBuilderReviewSettingsStep} from './ReviewSettingsStep/AgentBuilderReviewSettingsStep';
import {AgentType} from '../../../shared/agent';
import {AgentFieldTypeProvider} from './AgentFieldTypeContext';
import {AgentBuilderEditLegacyTeamSizeFieldStep} from './AgentBuilderEditLegacyTeamSizeFieldStep';
import {AgentBuilderEditResearchFieldStep} from './AgentBuilderEditResearchFieldStep';
import {useResetAgentBuilder} from './hooks/useResetAgentBuilder';

export const AgentBuilderModal = ({
  isOpen,
  onClose,
  agentId,
  initialStep,
  initialType,
}: {
  isOpen: boolean;
  onClose: () => void;
  agentId?: ObjectId;
  initialStep?: AgentBuilderStep;
  initialType?: AgentType;
}) => {
  const resetAgentBuilder = useResetAgentBuilder();

  return (
    <Modal
      isCentered={true}
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={resetAgentBuilder}
      size="full"
    >
      <ModalOverlay />
      <ModalContent>
        <AgentBuilderContent
          agentId={agentId}
          initialStep={initialStep}
          initialType={initialType}
        />
      </ModalContent>
    </Modal>
  );
};

const steps = {
  setName: <AgentBuilderSetNameStep />,
  selectAccounts: <AgentBuilderSelectAccountsStep />,
  editLegacyResearchField: (
    <AgentFieldTypeProvider fieldType="legacyResearch">
      <AgentBuilderEditLegacyResearchFieldStep />
    </AgentFieldTypeProvider>
  ),
  editResearchField: (
    <AgentFieldTypeProvider fieldType="research">
      <AgentBuilderEditResearchFieldStep />
    </AgentFieldTypeProvider>
  ),
  editAnalystField: (
    <AgentFieldTypeProvider fieldType="analyst">
      <AgentBuilderEditAnalystFieldStep />
    </AgentFieldTypeProvider>
  ),
  editLegacyTeamSizeField: (
    <AgentFieldTypeProvider fieldType="legacyTeamSize">
      <AgentBuilderEditLegacyTeamSizeFieldStep />
    </AgentFieldTypeProvider>
  ),
  analyzeLists: <AgentBuilderAnalyzeListsStep />,
  reviewSettings: <AgentBuilderReviewSettingsStep />,
} satisfies Record<AgentBuilderStep, React.ReactNode>;

export const AgentBuilderContent = ({
  agentId,
  initialStep,
  initialType,
}: {
  agentId?: ObjectId;
  initialStep?: AgentBuilderStep;
  initialType?: AgentType;
}) => {
  const agent = useAgent(agentId);
  const {updateServerState, setType} = useAgentBuilderStore();
  const {step, setStep} = useAgentBuilderNavStore();

  useEffect(() => {
    if (agent) {
      updateServerState({agent, updateMode: 'ifNoChanges'});
    }
  }, [agent, updateServerState]);

  useEffect(() => {
    if (initialStep) {
      setStep(initialStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep]);

  useEffect(() => {
    if (initialType) {
      setType(initialType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialType]);

  return (
    <>
      <ModalBody as={Flex} flexDirection="column" p="0">
        {steps[step]}
      </ModalBody>
    </>
  );
};
